import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useEventListener, useAccountData } from "../../../Hooks";
import { useToast } from "../../../Hooks/toast";
import { useBalance } from "../../../Hooks/useBalance";
import { useAuth } from "../../../Contexts/AuthContext";
import { useSupport } from "../../../Contexts/SupportContext";
import { AppBar, Box, Drawer, IconButton, Toolbar, useMediaQuery, useTheme } from "@mui/material";
import { Redirect, Route, Link as RouterLink, Switch } from "react-router-dom";
import { IMenuRoute, routes } from '../../../Constants/Routes'
import { CompanyLogo } from "../../Atoms";
import DesktopBalance from "../../Molecules/BalanceCard/DesktopBalance";
import { HeaderContent, MenuNavigation } from "../../Molecules";
import {
  ChevronLeft,
  Menu
} from '@mui/icons-material';

const PrivateRouter = ({ route }: { route: IMenuRoute }) => {

  const { hasPermission } = useAuth();

  if(route.permission && !hasPermission(route.permission)) {
    return <Redirect to={'/'}/>
  }

  return (
    <>
      { route.component() }
    </>
  )
}

const PrivateApp = () => {
  const { state: accountData, handleGET: fetchAccountGET } = useAccountData();
  const { getBalance, balanceValue, loading } = useBalance() as any;
  const { addToast } = useToast();

  const { user, logout } = useAuth();
  const { refreshUnread } = useSupport();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);

  if(!user.isAutenticated) {
    return <Redirect to={'/auth'} />
  }

  if(!user.accepted_terms) {
    return <Redirect to={'/auth/accepted-terms'} />
  }

  const sessionDisconnect = useCallback(() => {
    logout();
    addToast({
      type: "info",
      title: "Sessão desconectada",
      description:
        "Para sua segurança você foi desconectado da aplicação, faça login novamente.",
    });
  }, []);

  useEffect(() => {
    fetchAccountGET();
    // Fetch unread support messages on app entry when authenticated
    if (user?.isAutenticated) {
      refreshUnread({ notify: true });
    }
  }, []);

  useEventListener("error-401", sessionDisconnect, window as any);

const boxStyles = useMemo(() => {
    if (isMobile) {
      return {
        gridTemplateAreas: `
          "toolbar"
          "main"
        `,
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr'
      };
    }

    return {
      gridTemplateAreas: `
          "drawer toolbar"
          "drawer main"
        `,
      gridTemplateColumns: '350px 1fr',
      gridTemplateRows: 'auto 1fr'
    };
  }, [isMobile]);

  return (
    <Box
      display='grid'
      overflow='hidden'
      {...boxStyles}
    >
      <Drawer
        onClose={() => setOpen(false)}
        variant={isMobile ? 'temporary' : 'permanent'}
        anchor="left"
        keepMounted={true}
        sx={{ gridArea: 'drawer' }}
        open={open || !isMobile}
      >
        <IconButton onClick={() => setOpen(false)}>

        </IconButton>
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 40px 20px 40px' }}>
          <RouterLink to='/'>
            <CompanyLogo height={50} width={200} />
          </RouterLink>
          {
            isMobile && (
              <IconButton size="large" onClick={() => setOpen(false)}>
                <ChevronLeft />
              </IconButton>
            )
          }
        </Box>
        <div style={{ height: "100%" }}>
          <DesktopBalance
            accountBalanceData={balanceValue}
            refreshBalance={() => getBalance()}
            loadingRefreshBalance={loading}
          />
          <MenuNavigation />
        </div>
      </Drawer>
      <AppBar position="relative" sx={{ gridArea: 'toolbar', background: '#FFF' }} elevation={0}>
        <Toolbar>
          {isMobile && (
            <IconButton onClick={() => setOpen(true)}>
              <Menu />
            </IconButton>
          )}
          <HeaderContent logout={logout} accountData={accountData} />
        </Toolbar>
      </AppBar>
      <Box p={2} gridArea='main' component='main' width={'100%'} overflow='scroll'>
        <Switch>

          {routes.map(route => (

              <Route
                key={route.path}
                path={route?.path}
                exact={route?.exact ?? true}
              >
                <PrivateRouter route={route}/>
              </Route>
            ))}
        </Switch>
      </Box>
    </Box>
  );
};

export default PrivateApp;
